import { ICustomSeverity } from "../@types";

const MAX_VELOCITY = 100;
const MIN_VELOCITY = 0.05;
const MIN_PT_GAP = 0.005;

const RAW_LEAD_OUT_FRAC = 0.5;
const RAW_HIT_OUT_FRAC = 0.1;
const RAW_POST_OUT_FRAC = 0.5;
const MAX_COMBINED_OUT_FRAC = 0.5;

export interface Keyframe {
  in_ts: number;
  out_ts: number;
}

export interface BuildArgs {
  eventInTs: number;
  beatPeriod: number;
  severity: number;
  customSeverity: ICustomSeverity | null;
  clipInEnd: number;
}

export interface RampParams {
  velocity: number;
  slowMult: number;
  rud: number;
  ld: number;
  td: number;
  rdd: number;
}

export const calculateRampParameters = (
  inDur: number,
  outDur: number,
): RampParams => {
  const minMult = 1 / MAX_VELOCITY;
  const maxMult = 1 / MIN_VELOCITY;

  let rud = inDur / 4;
  let ld = inDur / 4;
  let td = inDur / 4;
  let rdd = inDur / 4;
  let slowMult = (outDur - rdd) / (ld + td + rdd);

  if (slowMult < minMult || slowMult > maxMult) {
    slowMult = Math.max(minMult, Math.min(maxMult, slowMult));
    const denom = 1 - slowMult;
    if (Math.abs(denom) < 1e-12) {
      return { velocity: 1, slowMult: 1, rud: inDur, ld: 0, td: 0, rdd: 0 };
    }
    const rampDurs = (2 * (outDur - slowMult * inDur)) / denom;
    rud = rampDurs / 2;
    rdd = rampDurs / 2;
    ld = (inDur - rampDurs) / 2;
    td = (inDur - rampDurs) / 2;

    if (rud < 0 || ld < 0 || td < 0 || rdd < 0) {
      return { velocity: 1, slowMult: 1, rud: inDur, ld: 0, td: 0, rdd: 0 };
    }
  }

  return { velocity: 1 / slowMult, slowMult, rud, ld, td, rdd };
};

const lerp = (t: number, lo: number, hi: number): number => lo + t * (hi - lo);

export const velFromLever = (t: number): number => {
  if (t <= 0.5) return lerp(t / 0.5, 0.1, 1.0);
  return lerp((t - 0.5) / 0.5, 1.0, 5.0);
};

const clampDurationLever = (v: number): number =>
  Math.max(0.01, Math.min(1.0, v));

const clampLever = (v: number): number => Math.max(0.0, Math.min(1.0, v));

interface OverrideOutputs {
  leadOut: number;
  hitOutHalf: number;
  postOut: number;
}

const resolveOverrideOutputs = (
  cs: ICustomSeverity,
  B: number,
): OverrideOutputs => {
  const leadOut = clampDurationLever(cs.lead_duration) * RAW_LEAD_OUT_FRAC * B;
  const hitOutHalf = clampDurationLever(cs.hit_duration) * RAW_HIT_OUT_FRAC * B;
  const postOut = clampDurationLever(cs.post_duration) * RAW_POST_OUT_FRAC * B;

  const total = leadOut + 2 * hitOutHalf + postOut;
  const budget = MAX_COMBINED_OUT_FRAC * B;
  if (total > budget && total > 0) {
    const s = budget / total;
    return {
      leadOut: leadOut * s,
      hitOutHalf: hitOutHalf * s,
      postOut: postOut * s,
    };
  }
  return { leadOut, hitOutHalf, postOut };
};

interface CSDerived {
  leadDur: number;
  leadVel: number;
  hitDur: number;
  hitVel: number;
  postDur: number;
  postVel: number;
}

const csDerived = (cs: ICustomSeverity, B: number): CSDerived => {
  const lead = clampLever(cs.lead);
  const hit = clampLever(cs.hit);
  const post = clampLever(cs.post);

  const leadVel = velFromLever(lead);
  const hitVel = velFromLever(hit);
  const postVel = velFromLever(post);

  const overrides = resolveOverrideOutputs(cs, B);

  return {
    leadDur: overrides.leadOut * leadVel,
    leadVel,
    hitDur: overrides.hitOutHalf * hitVel,
    hitVel,
    postDur: overrides.postOut * postVel,
    postVel,
  };
};

interface HitTimings {
  hitIn: number;
  hitOut: number;
  leadIn: number;
}

const hitTimingsFromBeat = (B: number): HitTimings => ({
  hitIn: Math.min(0.08, 0.2 * B),
  hitOut: Math.min(0.04, 0.1 * B),
  leadIn: Math.min(0.3, 0.75 * B),
});

const hitTimingsFromCustom = (d: CSDerived): HitTimings => ({
  hitIn: d.hitDur,
  hitOut: d.hitVel > 0 ? d.hitDur / d.hitVel : 0,
  leadIn: d.leadDur + d.hitDur,
});

const postIn = (severity: number, d: CSDerived | null, B: number): number => {
  if (d) return d.postDur;
  return Math.min(0.1 + 0.4 * severity, 0.5 * B);
};

const postOutFixed = (
  severity: number,
  d: CSDerived | null,
  B: number,
): number => {
  if (d) return d.postVel > 0 ? d.postDur / d.postVel : 0;
  return postIn(severity, null, B) + 0.6 * severity * B;
};

const leadOutFixed = (
  severity: number,
  d: CSDerived | null,
  t: HitTimings,
): number => {
  if (d) {
    const hitOut = d.hitVel > 0 ? d.hitDur / d.hitVel : 0;
    return d.leadVel > 0 ? d.leadDur / d.leadVel + hitOut : 0;
  }
  return t.leadIn - t.hitIn + t.hitOut;
};

const round6 = (v: number): number => Math.round(v * 1e6) / 1e6;

export const buildSingleEventKeyframes = (args: BuildArgs): Keyframe[] => {
  const {
    eventInTs,
    beatPeriod: B,
    severity,
    customSeverity,
    clipInEnd,
  } = args;
  if (B <= 0 || clipInEnd <= 0) return [];

  if (eventInTs < 0 || eventInTs > clipInEnd) {
    const tailIn = Math.max(clipInEnd, 0.02);
    return [
      { in_ts: 0, out_ts: 0 },
      { in_ts: round6(tailIn), out_ts: round6(tailIn) },
    ];
  }

  const d = customSeverity ? csDerived(customSeverity, B) : null;
  const t = d ? hitTimingsFromCustom(d) : hitTimingsFromBeat(B);

  const leadInOff = t.leadIn;
  const leadOutOff = leadOutFixed(severity, d, t);
  const pIn = postIn(severity, d, B);
  const pOut = postOutFixed(severity, d, B);

  // Preamble (origin → approach) plays at realtime. The SVG has no beat to
  // land on, so the event is placed at whatever output time keeps the segment
  // before the lead at velocity 1 — diverging from nublu, which pins the
  // event to out_ts = B and produces an unwanted long slow-mo intro for
  // events that occur near the start of the input.
  const approachIn = Math.max(0, eventInTs - leadInOff);
  const approachOut = approachIn;
  const eventOut = approachOut + leadOutOff;

  const candidates: Keyframe[] = [
    { in_ts: 0, out_ts: 0 },
    { in_ts: approachIn, out_ts: approachOut },
    { in_ts: eventInTs - t.hitIn, out_ts: eventOut - t.hitOut },
    { in_ts: eventInTs + t.hitIn, out_ts: eventOut + t.hitOut },
    { in_ts: eventInTs + t.hitIn + pIn, out_ts: eventOut + t.hitOut + pOut },
  ];

  const accepted: Keyframe[] = [];
  for (const p of candidates) {
    if (
      accepted.length === 0 ||
      p.in_ts - accepted[accepted.length - 1].in_ts >= MIN_PT_GAP
    ) {
      accepted.push(p);
    }
  }

  const last = accepted[accepted.length - 1];
  const tailIn = Math.max(clipInEnd, last.in_ts + 0.02);
  const tailOut = last.out_ts + (tailIn - last.in_ts);
  accepted.push({ in_ts: tailIn, out_ts: tailOut });

  return accepted.map((p) => ({
    in_ts: round6(p.in_ts),
    out_ts: round6(p.out_ts),
  }));
};

const invertTrapezoid = (outT: number, r: RampParams): number => {
  const { slowMult, rud, ld, td, rdd } = r;
  const aOut = (rud * (1 + slowMult)) / 2;
  const bOut = aOut + slowMult * (ld + td);

  if (outT <= aOut) {
    if (rud <= 0 || Math.abs(slowMult - 1) < 1e-12) return outT;
    const k = (slowMult - 1) / rud;
    if (Math.abs(k) < 1e-12) return outT;
    const disc = 1 + 2 * k * outT;
    const sqrtDisc = Math.sqrt(Math.max(0, disc));
    return (-1 + sqrtDisc) / k;
  }
  if (outT <= bOut) {
    if (slowMult <= 0) return rud;
    return rud + (outT - aOut) / slowMult;
  }
  if (rdd <= 0 || Math.abs(slowMult - 1) < 1e-12) {
    return rud + ld + td + (outT - bOut);
  }
  const residue = outT - bOut;
  const k = (1 - slowMult) / rdd;
  if (Math.abs(k) < 1e-12) {
    return rud + ld + td + (slowMult > 0 ? residue / slowMult : 0);
  }
  const disc = slowMult * slowMult + 2 * k * residue;
  const sqrtDisc = Math.sqrt(Math.max(0, disc));
  const u = (-slowMult + sqrtDisc) / k;
  return rud + ld + td + u;
};

export const outputToInput = (outT: number, keyframes: Keyframe[]): number => {
  if (keyframes.length === 0) return 0;
  if (outT <= keyframes[0].out_ts) return keyframes[0].in_ts;
  const last = keyframes[keyframes.length - 1];
  if (outT >= last.out_ts) return last.in_ts;

  for (let i = 1; i < keyframes.length; i++) {
    const prev = keyframes[i - 1];
    const curr = keyframes[i];
    if (outT >= prev.out_ts && outT <= curr.out_ts) {
      const inDur = curr.in_ts - prev.in_ts;
      const outDur = curr.out_ts - prev.out_ts;
      const localOut = outT - prev.out_ts;

      if (outDur <= 0) return prev.in_ts;
      if (inDur <= 0) return prev.in_ts;
      if (Math.abs(inDur - outDur) < 1e-9) {
        return prev.in_ts + localOut;
      }

      const r = calculateRampParameters(inDur, outDur);
      const localIn = invertTrapezoid(localOut, r);
      return prev.in_ts + Math.min(inDur, Math.max(0, localIn));
    }
  }
  return last.in_ts;
};

export const totalOutputDuration = (keyframes: Keyframe[]): number =>
  keyframes.length > 0 ? keyframes[keyframes.length - 1].out_ts : 0;
